/**
 * Shared type surface for `@takeover/sdk` consumers.
 *
 * Structurally compatible with the app's internal `SeatData` / V2 types so the
 * embed can be dropped into the takeover.fun app itself without adapter layers.
 * Keep these in sync with `takeover-app/src/lib/types/v2.ts` and
 * `takeover-app/src/components/grid/seat-cell.tsx`.
 */

export type Address = `0x${string}`;
export type BigIntString = string;

/**
 * Board-facing seat projection used by `<TakeoverGrid />`. Scoped to the
 * fields the embeddable canvas + tooltip actually read.
 */
export interface SeatData {
  seatIndex: number;
  holder: string | null;
  holderAvatar?: string | null;
  holderName?: string | null;
  selfAssessedPrice: bigint;
  balance: bigint;
  runwaySeconds: number;
  lastUpdateTime?: number;
  claimedAt?: number;
  lastPriceChangeTime?: number;
  createdAt?: number;
  rank?: number;
  holderLevel?: number;
  isOwn?: boolean;
  /** Deposit fully consumed on-chain — holder still set but seat is effectively empty. */
  isForfeited?: boolean;
  /** Seat is still held by the grid creator (initial mint not yet sold). */
  isCreatorSeat?: boolean;
  /** Unix timestamp when anti-snipe priority window ends. */
  antiSnipeEndsAt?: number;
  /** Deposit exhausted client-side but no on-chain pokeTax has fired yet. */
  isPendingForfeit?: boolean;
  /** Seat has an unclaimed whitelist reservation. */
  isReserved?: boolean;
  reservedFor?: string;
  claimDeadline?: number;
  reservedForName?: string;
  reservedForAvatar?: string;
  taxRateBps?: bigint;
}

export interface V2Grid {
  id: string;
  creator: Address;
  totalSeats: number;
  taxRateBps: number;
  taxToken: Address;
  taxTokenDecimals?: number;
  yieldToken: Address;
  yieldTokenDecimals: number;
  graduated: boolean;
  paused: boolean;
  deprecated: boolean;
  managerAddress: Address;
  activeSeatCount: number;
  seatsSoldFromCreator: number;
  uniqueHolderCount: number;
  takeoverCost: BigIntString;
  floorPrice: BigIntString;
  averagePrice: BigIntString;
  ceilingPrice: BigIntString;
  firstSeatClaimedAt: number | null;
  lastSeatPurchasedAt: number | null;
  createdAt: number;
  name?: string;
  imageUrl?: string;
  description?: string;
}

export interface V2GridSeat {
  id: string;
  gridId: string;
  seatIndex: number;
  holder: Address | null;
  selfAssessedPrice: BigIntString;
  deposit: BigIntString;
  lastTaxTime: number;
  lastPriceChangeTime: number;
  heldSince: number;
  isActive: boolean;
  createdAt: number;
  createdTxHash: string;
  holderIdentity?: {
    displayName?: string;
    avatarUrl?: string;
    level?: number;
  };
  /** Authoritative on-chain currentBalance from RPC (stringified bigint, may be negative). */
  rpcCurrentBalance?: string;
}

/**
 * Slim projection of the `/api/v1/grid/[ugm]/[gridId]` payload (the `data`
 * field of the `{ data, meta }` envelope). Only the fields the embed reads.
 */
export interface V2GridPageData {
  grid: V2Grid;
  seats: V2GridSeat[];
  reservations?: {
    seatIndex: number;
    claimant: string;
    claimantIdentity?: { displayName?: string; avatarUrl?: string; level?: number };
  }[];
  claimDeadline?: number;
  creatorIdentity?: {
    address: string;
    displayName?: string;
    avatarUrl?: string;
    level?: number;
  };
}

export interface GridApiResponse {
  data: V2GridPageData;
  meta: { timestamp: number; cached: boolean; cacheAge?: number };
}

/**
 * Grid list row returned by `/api/v1/grids` — kept in sync with the app route
 * `src/app/api/v1/grids/route.ts` for lobby + `useGrids()` consumers.
 */
export interface LobbyGridSummary {
  id: string;
  creator: string;
  totalSeats: number;
  taxRateBps: number;
  taxToken: string;
  taxTokenDecimals: number;
  yieldToken: string;
  yieldTokenDecimals: number;
  graduated: boolean;
  paused: boolean;
  deprecated: boolean;
  managerAddress: string;
  activeSeatCount: number;
  seatsSoldFromCreator: number;
  uniqueHolderCount: number;
  takeoverCost: string;
  floorPrice: string;
  averagePrice: string;
  ceilingPrice: string;
  firstSeatClaimedAt: number | null;
  lastSeatPurchasedAt: number | null;
  totalYieldReceived: string;
  totalTaxCollected: string;
  totalTaxWithdrawn: string;
  pendingTaxRevenue: string;
  totalYieldClaimed: string;
  estimatedPendingRewards: string;
  createdAt: number;
  totalTvlUsd?: string;
  totalTvlNative?: string;
  name?: string;
  imageUrl?: string;
  curated?: boolean;
  /** Soft-moderation flag set when the board is in the gateway's
   *  `banned_grids` table. Banned rows are NOT filtered out of the
   *  response — consumers decide the moderation UX (hide, dim,
   *  badge, surface as-is). The takeover-app's own lobby filters
   *  these client-side; SDK builders can choose otherwise. */
  banned?: boolean;
  /** Board-level description sourced from `pool_metadata.description`.
   *  Lobby cards and hover tooltips render this verbatim so they stay
   *  in sync with the board page's own metadata. Empty / absent →
   *  consumers fall back to the canonical "Land holders get access…"
   *  copy. */
  description?: string;
  yield7d?: string;
  /** Indexer's last-rolled-up timestamp for `yield7d` (unix sec). Lobby
   *  consumers feed this into `materializedRewardsRawOrZeroIfStale` so
   *  a 7d window that hasn't seen activity in 8+ days renders as `$0`
   *  instead of the last non-decayed sum (which otherwise reads as a
   *  lifetime number on long-idle pools). */
  yield7dUpdatedAt?: number | null;
  rewards1d?: string;
  rewards7d?: string;
  /** Count of non-deleted chat messages posted in this board's chat
   *  room over the trailing 7 days. Populated by the gateway from a
   *  cached Postgres aggregate (`getMessageCountsByRoomSince`); absent
   *  / zero when the room has had no traffic. Surfaced on the lobby's
   *  "Chat" sort and folded into the composite "Activity" score so
   *  chat-only successes (boards where holders pay tax just to be in
   *  chat, with no yield flowing) actually rank up.
   *
   *  Reserved-room aliases (`'boardroom'`, `'megapot-grid'`) are
   *  resolved server-side before emission — the SDK consumer always
   *  sees the count attributed to the on-chain grid id, so client
   *  code never needs to know about the reserved-label mapping. */
  chatMessages7d?: number;
  creatorIdentity?: { address: string; displayName?: string; avatarUrl?: string };
  assets?: Array<{
    id?: string;
    active?: boolean;
    assetType?: string | null;
    platform?: string | null;
    token0Symbol?: string | null;
    token1Symbol?: string | null;
  }>;
  adapterType?: 'flaunch' | 'uniswap-v3' | 'uniswap-v4' | 'aerodrome' | 'custom' | null;
  pairLabel?: string;
  creatorAddress?: string;
  adapterNotes?: Record<string, { title?: string; description?: string }>;
  boardPlinth?: string;
  boardSkybox?: string;
}

export interface GridRef {
  ugmAddress: string;
  gridId: string;
  label?: string;
}

export interface TakeoverSDKConfig {
  apiBaseUrl: string;
  chainId: number;
  rpcUrl: string | undefined;
  grids: GridRef[];
  privyAppId: string | undefined;
  chatBackendUrl?: string;
}

/** Constants shared with the app's V2 runtime. */
export const V2_CONSTANTS = {
  TOTAL_SEATS: 100,
  GRID_SIZE: 10,
  SECONDS_PER_WEEK: 7 * 24 * 60 * 60,
  USDC_DECIMALS: 6,
  TAX_RATE_DENOMINATOR: 10000,
  ANTI_SNIPE_WINDOW: 5 * 60,
} as const;
